/**
 * @module builder
 */
import get from 'lodash/get'
import uniq from 'lodash/uniq'
import type { OptionsDocument } from 'paintscout'
// Deep imports, not the barrels: this module is reached from calculateQuote (through the tiered
// packages product swap), and both barrels re-export calculate.ts, closing a cycle back onto it.
import { getQuoteType } from '../../quote/util/get-quote-type/get-quote-type'
import { getObjectLabels } from '../../util/get-object-labels/get-object-labels'
import type { Product } from '..'

export function getProducts(args: {
  options: OptionsDocument
  quoteType?: string
  rateType?: string
  inactive?: boolean
  canDeactivate?: boolean
}): Product[] {
  const { options, inactive, rateType, canDeactivate = true } = args
  const { products } = options.options
  const { unit } = getObjectLabels({ options })
  const quoteType = getQuoteType(args)

  const order =
    products.quoteTypeOrder && products.quoteTypeOrder[quoteType]
      ? uniq([...products.quoteTypeOrder[quoteType], ...products.order])
      : uniq(products.order)

  return order
    .map((orderItem) => {
      const item = products.values[orderItem]
      if (!item) {
        return null
      }

      let quoteTypes
      if (item.quoteTypes) {
        quoteTypes = item.quoteTypes
      } else {
        quoteTypes = [get(item, 'int_ext.value')]
      }

      let rateTypes = []
      if (item.rateTypes) {
        rateTypes = item.rateTypes
      } else if (item.rate_type) {
        // Handle depreciated field here until we can remove
        if (item.rate_type.sqft_floor) {
          rateTypes.push('sqftFloor')
        }
        if (item.rate_type.sqft_ceiling) {
          rateTypes.push('sqftCeiling')
        }
        if (item.rate_type.sqft_walls) {
          rateTypes.push('sqftWalls')
        }
      }

      const unitLabel = item?.unitLabel ?? 'gal'
      let sublabel = ``
      if (item.coverage_rate) {
        sublabel = `${item.coverage_rate} ${unit.abbreviation.square}/${unitLabel}`
      } else if (item.quantity_coverage) {
        sublabel = `${item.quantity_coverage} ${unitLabel}/item`
      } else if (item.lnft_coverage) {
        sublabel = `${item.lnft_coverage} ${unit.abbreviation.linear}/${unitLabel}`
      }

      const option: Product = {
        key: orderItem,
        active: item?.active ?? true,
        label: item.label,
        unitLabel,
        coverage: item.coverage_rate,
        lnftCoverage: item.lnft_coverage,
        quantityCoverage: item.quantity_coverage,
        price: item.price,
        quoteTypes,
        rateTypes,
        sublabel
      }

      if (canDeactivate && !inactive && item.active !== true) {
        return null
      } else if (inactive) {
        option.active = !!item.active
      }

      if (
        rateType &&
        rateType !== 'all' &&
        (!item.rateTypes || item.rateTypes?.indexOf(rateType) === -1) &&
        item.rateTypes?.indexOf('all') !== 0
      ) {
        return null
      }

      if (!quoteType || quoteType === 'all' || quoteTypes.indexOf('all') > -1 || quoteTypes.indexOf(quoteType) > -1) {
        return option
      }

      return null
    })
    .filter((item) => item)
}
